import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Col, FormGroup, Label, Row, Container, Button } from "reactstrap";
import { PageHeader } from "@/CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, FloppyDiskIcon } from "@hugeicons/core-free-icons";
import { useSystem } from "@/utils/hooks/useLocation";
import {
  useSearchPilgrimMutation,
  useCreateIncidentReportMutation,
  useUpdateIncidentReportMutation,
  useGetIncidentReportQuery,
} from "@/services/reports";
import { Divider, Tabs } from "antd";
import { toast } from "react-toastify";
import { ActionsFooter } from "@/Component/ActionsFooter";
import OtherReportTab from "./components/OtherReportTab";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import usePermissions from "@/utils/hooks/usePermissions";
import { permissions } from "@/utils/constants";
import dayjs from "dayjs";
import PilgrimReportTab from "./components/PilgrimReportTab";

/** Normalize ID to number so Ant Design Select matches option value and shows label instead of raw ID */
const toOptionId = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

const mapReportToFormValues = (report, getLocalizedText) => {
  if (!report) return {}
  const pilgrim = report.pilgrim || {};
  const genderMap = { "1": "male", "2": "female", male: "male", female: "female" };
  return {
    search_key_type: "border_number",
    pilgrim_id_or_passport: pilgrim?.border_number ?? "",
    pilgrim_id: toOptionId(report.pilgrim_id ?? pilgrim.id),
    pilgrim_name: getLocalizedText(pilgrim.full_name_ar, pilgrim.full_name_en) ?? "",
    nationality: toOptionId(report.nationality_id ?? pilgrim.nationality_id),
    gender: genderMap[report.gender ?? pilgrim.gender] ?? null,
    service_center: toOptionId(report.service_center_id ?? pilgrim.service_center_id),
    housing_contract: toOptionId(report.contract_id ?? pilgrim.contract_id),
    campaign_number: report.campaign_number ?? pilgrim.campaign_number ?? "",
    case_type_id: toOptionId(report.case_type?.id ?? report.case_type_id),
    case_status_id: toOptionId(report.case_status_id ?? report.case_status?.id),
    case_authority_id: toOptionId(report.case_authority_id ?? report.case_authority?.id),
    report_authority_id: toOptionId(report.report_authority_id ?? report.report_authority?.id),
    report_status_id: toOptionId(report.report_status_id ?? report.report_status?.id),
    document_type_id: toOptionId(report.document_type_id ?? report.document_type?.id),
    card_status_id: toOptionId(report.card_status_id ?? report.card_status?.id),
    document_number: report.document_number ?? "",
    description: report.description ?? "",
    // action_taken: report.action_taken ?? "",
    reported_at: report.reported_at ? String(report.reported_at).trim().slice(0, 19) : null,
    body_location_id: toOptionId(report.body_location_id ?? report.body_location?.id),
    resolved_at: Boolean(report.resolved_at),
    resolve_reason: report.resolve_reason ?? "",
    full_name: report.reported_person_name ?? "",
    id_number: report.reported_person_id_number ?? "",
    other_mobile_number: report.reported_person_phone_number ?? "",
    reported_person_name: report.reported_person_name ?? "",
    reported_person_id_number: report.reported_person_id_number ?? "",
    reported_person_phone_number: report.reported_person_phone_number ?? "",
    bag_number: report.pilgrim.bag_number ?? "",
    documents: [],
  };
};

const CreateReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: reportId } = useParams();
  const { currentSystem } = useSystem();
  const { getLocalizedText } = useLocalizedText();
  const {
    hasAnyPermission
  } = usePermissions();
  const [activeTab, setActiveTab] = useState("pilgrim");
  const [, setPilgrimFound] = useState(false);
  const isEditMode = Boolean(reportId);

  const searchPilgrimPerm = useMemo(
    () =>
      currentSystem === "makkah"
        ? permissions.search_pilgrims_for_makkah_incident_reports
        : permissions.search_pilgrims_for_madina_incident_reports,
    [currentSystem]
  );
  const canSearchPilgrims = hasAnyPermission([searchPilgrimPerm]);

  const searchPilgrim = useSearchPilgrimMutation({ system: currentSystem });
  const createReport = useCreateIncidentReportMutation({ system: currentSystem });
  const updateReport = useUpdateIncidentReportMutation({ system: currentSystem });
  const { data: reportResponse, isLoading: isLoadingReport } = useGetIncidentReportQuery(reportId, {
    system: currentSystem,
  });
  const report = reportResponse?.data?.data ?? reportResponse?.data;
  const hasPilgrimInReport =
    isEditMode && report && report.pilgrim && typeof report.pilgrim === "object";

  const form = useForm({
    defaultValues: {},
  });

  const { control, watch, handleSubmit, reset, setValue } = form;
  const healthCertificateInputRef = useRef(null);
  const appliedReportIdRef = useRef(null);

  useEffect(() => {
    if (!reportId) {
      appliedReportIdRef.current = null;
      return;
    }
    if (!report) return;
    if (appliedReportIdRef.current === reportId) return;
    appliedReportIdRef.current = reportId;
    reset(mapReportToFormValues(report, getLocalizedText));
    setActiveTab(report.pilgrim && typeof report.pilgrim === "object" ? "pilgrim" : "other");
  }, [reportId, report, reset, getLocalizedText]);

  const handleMutationError = useCallback((err) => {
    const errors = err?.response?.data?.errors;
    if (errors && typeof errors === "object") {
      Object.values(errors)
        .flat()
        .forEach((msg) => toast.error(msg));
    }
  }, []);

  const onSubmit = (data) => {
    if (isEditMode) {
      updateReport.mutate(
        { id: reportId, ...data },
        {
          onSuccess: () => navigate(`/app/${currentSystem}/reports`),
          onError: handleMutationError,
        }
      );
    } else {
      createReport.mutate(data, {
        onSuccess: () => navigate(`/app/${currentSystem}/reports`),
        onError: handleMutationError,
      });
    }
  };

  const isSubmitting = createReport.isPending || updateReport.isPending;

  const onCancel = () => {
    navigate(`/app/${currentSystem}/reports`);
  };

  const handleSearch = () => {
    if (!canSearchPilgrims) return;
    const key = form.getValues("search_key_type");
    const value = form.getValues("pilgrim_id_or_passport");
    if (!value) return;
    searchPilgrim.mutate(
      { key, value },
      {
        onSuccess: (res) => {
          const data = res?.data?.data;
          if (!data) return;
          const name = getLocalizedText(data.full_name_ar, data.full_name_en);
          const genderMap = { "1": "male", "2": "female" };
          form.setValue("pilgrim_id", data.id ?? data.pilgrim_id ?? null);
          form.setValue("pilgrim_name", name || "");
          form.setValue("nationality", data.nationality_id ?? null);
          form.setValue("gender", genderMap[data.gender] || null);
          form.setValue("housing_contract", data.contract_id ?? null);
          form.setValue("service_center", data.service_center_id ?? null);
          form.setValue("campaign_number", data.campaign_number ?? "");
          if (currentSystem === "madina") {
            form.setValue(
              "bag_number",
              data.bag_number ?? data.bag_number_medina ?? ""
            );
          }
          setPilgrimFound(true);
        },
      }
    );
  };

  const fieldCol = { xs: 12, md: 6, lg: 4 };
  const threeCol = { xs: 12, md: 6, lg: 4 };
  const fullWidthCol = { xs: 12 };

  const FormField = ({ name, label, required, children, colProps = fieldCol, errorInside = false }) => (
    <Col {...colProps}>
      <FormGroup className="mb-3">
        <Label className="fw-bold" for={name}>
          {label} {required && <span className="text-danger">*</span>}
        </Label>
        {children}
      </FormGroup>
    </Col>
  );

  const existingDocuments = report?.documents ?? [];

  const caseDetailsProps = {
    control,
    FormField,
    watch,
    setValue,
    threeCol,
    fullWidthCol,
    healthCertificateInputRef,
    existingDocuments,
    reportId: reportId ?? null,
  };
  const tabsActiveKey =
    isEditMode && report ? (hasPilgrimInReport ? "pilgrim" : "other") : activeTab;

  const pilgrimTab = {
    key: "pilgrim",
    label: t("PILGRIM_DATA_TAB"),
    children: (
      <PilgrimReportTab
        form={form}
        control={control}
        FormField={FormField}
        watch={watch}
        setValue={setValue}
        onSearch={handleSearch}
        searchPilgrim={searchPilgrim}
        canSearchPilgrims={canSearchPilgrims}
        threeCol={threeCol}
        fullWidthCol={fullWidthCol}
        healthCertificateInputRef={healthCertificateInputRef}
        activeTabKey={tabsActiveKey}
        existingDocuments={existingDocuments}
        reportId={reportId ?? null}
        isEditMode
      />
    ),
  };
  const otherTab = {
    key: "other",
    label: t("OTHER_ENTITIES_REPORT_TAB"),
    children: <OtherReportTab {...caseDetailsProps} />,
  };
  const tabItems =
    isEditMode && report
      ? hasPilgrimInReport
        ? [pilgrimTab]
        : [otherTab]
      : [pilgrimTab, otherTab];

  if (isEditMode && isLoadingReport) {
    return (
      <div>
        <PageHeader title={t("EDIT_REPORT")} backTo={`/app/${currentSystem}/reports`} sticky={false} />
        <Container fluid className="pb-5">
          <div className="text-center py-5 text-muted">{t("LOADING") || "Loading..."}</div>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? t("EDIT_REPORT") : t("ADD_REPORT")}
        backTo={`/app/${currentSystem}/reports`}
        sticky={false}
      />
      <Container fluid className="pb-5">
                {/* Report metadata - read-only (label above, value below) */}
        {isEditMode && <Row className="g-3 mb-3">
          <Col {...threeCol} className="d-flex align-items-center">
            <div className="medium text-secondary mb-1 me-2">{t("REPORT_NUMBER")}:</div>
            <div className="fw-bold text-dark" style={{ fontSize: "1rem" }}>{report?.incident_number ?? "-"}</div>
          </Col>
          <Col {...threeCol} className="d-flex align-items-center">
            <div className="medium text-secondary mb-1 me-2">{t("REPORT_DATE")}:</div>
            <div className="fw-bold text-dark" style={{ fontSize: "1rem" }}>
              {dayjs(report?.reported_at).format("YYYY-MM-DD")}
            </div>
          </Col>
          <Col {...threeCol} className="d-flex align-items-center">
            <div className="medium text-secondary mb-1 me-2">{t("REPORT_CREATOR")}:</div>
            <div className="fw-bold text-dark" style={{ fontSize: "1rem" }}>{getLocalizedText(report?.creator?.name_ar, report?.creator?.name_en)}</div>
          </Col>
          {/* resolve person */}
          <Col {...threeCol} className="d-flex align-items-center">
            <div className="medium text-secondary mb-1 me-2">{t("RESOLVE_PERSON")}:</div>
            <div className="fw-bold text-dark" style={{ fontSize: "1rem" }}>{getLocalizedText(report?.resolved_by?.name_ar, report?.resolved_by?.name_en)}</div>
          </Col>
        </Row>}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs
            activeKey={tabsActiveKey}
            onChange={(key) => {
              setActiveTab(key);
              form.setValue("report_type", key);
              if (key === "other") form.clearErrors("pilgrim_id_or_passport");
            }}
            items={tabItems}
            className="mb-4"
          />
          <Divider />
          <ActionsFooter>
            <Button type="submit" className="btn-sm btn-dark bg-black text-white" disabled={isSubmitting}>
              <HugeiconsIcon size={16} icon={FloppyDiskIcon} className="me-2" />
              {isSubmitting ? t("LOADING") || "Loading..." : isEditMode ? t("UPDATE_REPORT") || t("SAVE_REPORT") : t("SAVE_REPORT")}
            </Button>
            <Button type="button" className="btn-sm btn btn-danger" onClick={onCancel}>
              <HugeiconsIcon size={16} icon={Cancel01Icon} className="me-2" />
              {t("CANCEL")}
            </Button>
          </ActionsFooter>
        </form>
      </Container>
    </div>
  );
};

export default CreateReport;
